<template>
  <div
    class="fts-form-element fts-form-element-theme"
    :class="[
      {'fw': fullWidthElement},
      {'form-element--signature': item && item.type == 29},
      comfortDesign ? 'comfort-form-design' : 'compact-form-design'
    ]"
  >
    <slot name="header" />
    <!-- custom:description -->
    <template v-if="item.input === 'description'">
      <fts-form-editor
        :key="item.id"
        :content.sync="item.content"
        class="fts-form-group"
        @input="$emit('design:change')"
        v-if="designMode"
      />
      <div
        :key="item.id"
        class="fts-form-group form-element-custom-description" 
        v-html="item.content"
        v-else
      ></div>
    </template>
    <!-- custom:image -->
    <template v-else-if="item.input == 'image'">
      <fts-file-upload
        :key="item.id"
        :vector-id="vectorId"
        :item="item.item"
        :form-data="item"
        image-width="auto"
        :image-height="200"
        class="fts-form-group"
        @input="$emit('design:change')"
        v-if="designMode"
      />
      <div
        :key="item.id"
        class="fts-form-group" 
        v-else-if="item.imageSource"
      >
        <img
          :src="getImagePath(item.imageSource.path)"
          class="form-element-custom-image"
          :alt="item.imageSource.name"
        />
      </div>
    </template>
    <!-- custom:button -->
    <div class="fts-form-group" v-else-if="item.input == 'button'">
      <fts-btn
        outline
        :color="'primary'"
        text-transform-init
        :disabled="disabled || item.readOnlyFormula || item.readOnly"
        @click="!(disabled || item.readOnlyFormula || item.readOnly) && clientAction(item)"
      >
        {{ item.label ? item.label : "(unnamed)" }}
      </fts-btn>
    </div>
    <!-- empty space -->
    <div class="form-element-custom-empty-space" v-else-if="item.input == 'empty'"></div>
    <!-- fts form element wrapper -->
    <div
      class="fts-form-element-wrapper fts-form-group"
      :class="{'form-group--error': !!error}"
      ref="element"
      v-else
    >
      <fts-form-label
        :icon="getIcon"
        :icon-right="fullWidthElement"
        :placement="!(fullWidthElement || comfortDesign) ? 'left' : 'top'"
        :maintain-label-height="maintainLabelHeight && !comfortDesign"
        :comfort-design="comfortDesign"
        :required="required && !formElementDisplay"
        v-if="item.input !== 'empty' && item.label !== false"
      >
        <slot name="formLabel" :label="labelDisplayName">{{labelDisplayName}}</slot>
      </fts-form-label>
      <div class="fts-form-helper" v-if="comfortDesign && item.helper">
        {{item.helper}}
      </div>
      <div class="form-element-content">
        <!-- slot -->
        <slot 
          name="formElement"
          :item="item"
          :form-data="formData"
          :onInput="onInput"
          :onBlur="onBlur"
          :error="error"
          v-if="isFormElementSlot"
        />
        <!-- display -->
        <fts-display
          :item="item"
          :form-data="formData"
          :vector-id="vectorId"
          :view="view"
          :share-form="shareForm"
          :comfort-design="comfortDesign"
          :form-settings="formSettings"
          :overflow-auto="overflowAuto"
          v-else-if="formElementDisplay"
        />
        <!-- custom formatted text -->
        <cleave
          class="fts-form-control"
          v-model="formData[item.fieldName]"
          :options="item.options"
          @input="onInput"
          v-else-if="item.input == 'formattedText'"
        />
        <!-- yes no -->
        <div
          class="form-element-checkbox-group"
          v-else-if="
            item.type == 1 ||
            formElementInput == 'boolean' ||
            formElementInput == 'toggle'
          "
        >
          <checkbox
            v-model="formData[item.fieldName]"
            color="var(--fts-primary)"
            :id="`checkbox-${uid}`"
            :size="20"
            class="form-element-checkbox"
            :class="{'form-element-checkbox--error': Boolean(error)}"
            @change="onInput"
          />
          <label
            :for="`checkbox-${uid}`"
            class="form-element-checkbox-label"
            v-if="item.checkboxLabel"
          >{{item.checkboxLabel}}</label>
        </div>
        <!-- integer -->
        <fts-integer
          :item="item"
          :form-data="formData"
          :min="item.min"
          :max="item.max"
          controls
          inline
          :error="Boolean(error)"
          @input="onInput"
          @blur="onBlur"
          v-else-if="
            item.type == 2 ||
            formElementInput == 'integer'
          "
        />
        <!-- number / money -->
        <fts-number-currency
          :item="item"
          :form-data="formData"
          :error="Boolean(error)"
          @input="onInput"
          @blur="onBlur"
          v-else-if="
            item.type == 3 ||
            item.type == 4 ||
            formElementInput == 'number' ||
            formElementInput == 'currency'
          "
        />
        <!-- date time -->
        <fts-date-time
          :min-datetime="item.minDatetime"
          :max-datetime="item.maxDatetime"
          :item="item"
          :form-data="formData"
          :comfort-design="comfortDesign"
          :error="Boolean(error)"
          @input="onInput"
          @close="validatorTouch()"
          v-else-if="
            item.type == 5 ||
            item.type == 6 ||
            formElementInput == 'dateTime' ||
            formElementInput == 'date' ||
            formElementInput == 'time'
          "
        />
        <!-- location -->
        <fts-location
          :item="item"
          :form-data="formData"
          :comfort-design="comfortDesign"
          :error="Boolean(error)"
          @input="onInput"
          @close="validatorTouch()"
          v-else-if="
            item.type == 9 ||
            formElementInput == 'location'
          "
        />
        <!-- file upload -->
        <fts-file-upload
          :item="item"
          :form-data="formData"
          :vector-id="vectorId"
          :comfort-design="comfortDesign"
          @input="onInput"
          v-else-if="
            item.type == 10 ||
            item.type == 11 ||
            item.type == 12 ||
            item.type == 13 ||
            formElementInput == 'imageUpload' ||
            formElementInput == 'imagesUpload' ||
            formElementInput == 'fileUpload' ||
            formElementInput == 'filesUpload'
          "
        />
        <!-- table:simple -->
        <fts-table-simple
          :vector-id="vectorId"
          :item="item"
          :form-data="formData"
          :validator="validator && validator[item.fieldName]"
          :comfort-design="comfortDesign"
          v-else-if="(item.type == 14 || formElementInput == 'table') && item.simpleMode === true"
        />
        <!-- table -->
        <fts-table-edit 
          :item="item" 
          :form-data="formData" 
          :vector-id="vectorId" 
          :validator="validator && validator[item.fieldName]" 
          :comfort-design="false" 
          :overflow-auto="overflowAuto" 
          :view="view" 
          :share-form="shareForm" 
          :design-mode="designMode" 
          :sticky-header="(formSettings && formSettings.tableStickyHeader) || false" 
          :offset="formSettings && formSettings.tableStickyOffset" 
          :hide-label-icon="formSettings && formSettings.hideLabelIcon" 
          @design:column="evt => $emit('design:column', evt)" 
          v-else-if="item.type == 14 || formElementInput == 'table'"
        />
        <!-- multiline text -->
        <fts-text-area
          :item="item"
          :form-data="formData"
          :rows="item.rows"
          :error="Boolean(error)"
          @input="onInput"
          @blur="onBlur"
          v-else-if="
            item.type == 16 ||
            formElementInput == 'textArea'
          "
        />
        <!-- username -->
        <fts-user
          :item="item"
          :form-data="formData"
          :comfort-design="comfortDesign"
          :error="Boolean(error)"
          @input="onInput"
          @blur="onBlur"
          v-else-if="
            item.type == 19 ||
            formElementInput == 'username'
          "
        />
        <!-- profile (module) -->
        <fts-user
          :item="item"
          :form-data="formData"
          :comfort-design="comfortDesign"
          :error="Boolean(error)"
          @input="onInput"
          @blur="onBlur"
          v-else-if="
            item.type == 20 ||
            formElementInput == 'dropDownUser'
          "
        />
        <fts-user
          :item="item"
          :form-data="formData"
          multiple
          :comfort-design="comfortDesign"
          :error="Boolean(error)"
          @input="onInput"
          @blur="onBlur"
          v-else-if="item.type == 21"
        />
        <!-- content -->
        <fts-content
          :item="item"
          :form-data="formData"
          :data="formData[item.fieldName]"
          :error="Boolean(error)"
          @input="onInput"
          @blur="onBlur"
          v-else-if="
            item.type == 22 ||
            formElementInput == 'content'
          "
        />
        <!-- context -->
        <fts-dropdown
          :item="item"
          :form-data="formData"
          multiple
          :comfort-design="comfortDesign"
          :error="Boolean(error)"
          @input="onInput"
          @blur="onBlur"
          v-else-if="
            item.type == 24 ||
            formElementInput == 'contextSelection'
          "
        />
        <!-- phone number -->
        <fts-phone-number
          :item="item"
          :form-data="formData"
          :comfort-design="comfortDesign"
          :error="Boolean(error)"
          @input="onInput"
          @blur="onBlur"
          v-else-if="
            item.type == 26 ||
            formElementInput == 'phone'
          "
        />
        <!-- select (radio) -->
        <fts-radio-group
          :comfort-design="comfortDesign"
          class="radio-group"
          :class="{'radio-group--compact': !comfortDesign}"
          v-else-if="
            Boolean(item.radio) && 
            (
              item.type == 27 ||
              formElementInput == 'select'
            )
          "
        >
          <fts-radio
            v-for="x in item.optionList"
            :key="x.id"
            :group-name="item.fieldName"
            :option-label="x.name"
            :option-value="x.name"
            v-model="formData[item.fieldName]"
            :comfort-design="comfortDesign"
          />
        </fts-radio-group>
        <!-- module (advanced template) -->
        <fts-radio-template
          :item="item.advancedTemplate"
          :connection-id="item.connectionId"
          :options="item.optionList"
          option-label-key="title"
          :value.sync="formData[item.fieldName]"
          :value-id.sync="formData[`${item.fieldName}Id`]"
          :comfort-design="comfortDesign"
          class="radio-group"
          :class="{'radio-group--compact': !comfortDesign}"
          v-else-if="item.type == 7 && item.advancedTemplate"
        />
        <!-- module ,select -->
        <fts-dropdown
          :item="item"
          :form-data="formData"
          :comfort-design="comfortDesign"
          :error="Boolean(error)"
          @input="onInput"
          @blur="onBlur"
          v-else-if="
            item.type == 7 ||
            item.type == 27 ||
            formElementInput == 'dropDown' ||
            formElementInput == 'select'
          "
        />
        <!-- locuses , tag-->
        <fts-dropdown
          :item="item"
          :form-data="formData"
          multiple
          :comfort-design="comfortDesign"
          :error="Boolean(error)"
          @input="onInput"
          @blur="onBlur"
          v-else-if="
            item.type == 8 ||
            item.type === 28
          "
        />
        <!-- signature -->
        <fts-signature
          :item="item"
          :form-data="formData"
          :vector-id="vectorId"
          @input="onInput"
          v-else-if="
            item.type == 29 ||
            formElementInput == 'signature'
          "
        />
        <!-- link -->
        <fts-link
          :placeholder="item.placeholder"
          autocomplete="form-element-input"
          v-model="formData[item.fieldName]"
          :error="Boolean(error)"
          @input="onInput"
          @blur="onBlur"
          v-else-if="item.type == 33 || formElementInput == 'link'"
        />
        <!-- list -->
        <fts-list
          :vector-id="vectorId"
          :item="item"
          :form-data="formData"
          :view="view"
          :validator="validator && validator[item.fieldName]"
          v-else-if="
            item.type == 15 ||
            formElementInput == 'list'
          "
        />
        <!-- date range -->
        <fts-period
          :item="item"
          :form-data="formData"
          v-else-if="
            item.type == 30 ||
            formElementInput == 'dateRange'
          "
        />
        <!-- toggle switch -->
        <fts-toggle-switch
          :item="{ fieldName: item.fieldName }"
          :form-data="formData"
          mat-class
          :label="false"
          :size="18"
          @change="onInput"
          v-else-if="formElementInput === 'toggleSwitch'"
        />
        <!-- input mask -->
        <fts-input-mask
          v-model="formData[item.fieldName]"
          :masked="masked"
          @input="onInput"
          @blur="onBlur"
          @unmask="$emit('unmask')"
          v-else-if="formElementInput === 'inputMask'"
        />
        <!-- password -->
        <input
          type="password"
          class="fts-form-control"
          :class="{'form-control--error': Boolean(error)}"
          autocomplete="new-password"
          v-model="formData[item.fieldName]"
          @input="onInput"
          @blur="onBlur"
          v-else-if="formElementInput === 'password'"
        />
        <!-- default -->
        <input
          type="text"
          class="fts-form-control"
          :class="{'form-control--error': Boolean(error)}"
          :placeholder="item.placeholder"
          autocomplete="form-element-input"
          v-model="formData[item.fieldName]"
          @input="onInput"
          @blur="onBlur"
          @paste="sanitizePastedText"
          @keyup.enter="onKeyupEnter"
          v-else
        />
        <!-- error message -->
        <div class="fts-form-error" v-if="!disableValidatorError && error">
          <slot name="errorMessage" :error="error">{{ error }}</slot>
        </div>
      </div>
    </div>

    <fts-action-page-modal ref="ftsActionPageModal" />
  </div>
</template>

<script>
import Vue from "vue";
import Cleave from "vue-cleave-component";
Vue.use(Cleave);

import webAppConfig from '../commons/webApp.config';
import commons from "../commons/commons";
import { clientStore } from "../store/clientStore";

import FTSFormLabel from "./FTSFormLabel";
import FTSDropdown from "./FTSDropdown";
import FTSTextArea from "./FTSTextArea";
import FTSDateTime from "./FTSDateTime";
import FTSInteger from "./FTSInteger";
import FTSLocation from "./FTSLocation";
import FTSFileUpload from "./FTSFileUpload";
import FTSTableEdit from "./FTSTableEdit";
import FTSTableSimple from './FTSTableSimple';
import FTSContent from "./FTSContent";
import FTSSignature from "./FTSSignature";
import FTSDisplay from "./FTSDisplay";
import FTSUser from "./FTSUser";
import FTSList from "./FTSList";
import FTSPeriod from "./FTSPeriod";
import FTSNumberCurrency from "./FTSNumberCurrency";
import FTSPhoneNumber from "./FTSPhoneNumber";
import FTSToggleSwitch from "./FTSToggleSwitch";
import FTSBtn from "./FTSBtn";
import FTSRadioTemplate from "./FTSRadioTemplate";
import FTSRadioGroup from "./FTSRadioGroup";
import FTSRadio from "./FTSRadio";
import FTSInputMask from "./FTSInputMask";
import FTSActionPageModal from "./FTSActionPageModal";
import FTSFormEditor from './form/FTSFormEditor';
import FTSLink from './FTSLink';

import Checkbox from "vue-material-checkbox";
/* temp (to-do remove) */
import Axios from 'axios';
/* end */

export default {
  components: {
    "fts-form-label": FTSFormLabel,
    "fts-dropdown": FTSDropdown,
    "fts-text-area": FTSTextArea,
    "fts-date-time": FTSDateTime,
    "fts-integer": FTSInteger,
    "fts-location": FTSLocation,
    "fts-file-upload": FTSFileUpload,
    "fts-table-edit": FTSTableEdit,
    "fts-table-simple": FTSTableSimple,
    "fts-content": FTSContent,
    "fts-signature": FTSSignature,
    "fts-display": FTSDisplay,
    "fts-user": FTSUser,
    "fts-list": FTSList,
    "fts-list": FTSList,
    "fts-period": FTSPeriod,
    "fts-number-currency": FTSNumberCurrency,
    "fts-phone-number": FTSPhoneNumber,
    "fts-toggle-switch": FTSToggleSwitch,
    "fts-btn": FTSBtn,
    "fts-radio-template": FTSRadioTemplate,
    "fts-radio-group": FTSRadioGroup,
    "fts-radio": FTSRadio,
    "fts-input-mask": FTSInputMask,
    "fts-action-page-modal": FTSActionPageModal,
    "fts-form-editor": FTSFormEditor,
    "fts-link": FTSLink,
    checkbox: Checkbox,
  },
  props: {
    vectorId: [String, Number],
    item: Object,
    formData: Object,
    validator: Object,
    validatorError: Function,
    username: String,
    context: Object,
    view: Boolean,
    shareForm: Boolean,
    masked: Boolean,
    comfortDesign: Boolean,
    overflowAuto: Boolean,
    disableValidatorError: Boolean,
    maintainLabelHeight: Boolean,
    designMode: Boolean,
    /**
     * @param {Object} formSettings
     * @param {boolean} formSettings.tableStickyHeader 
     * @param {Object} formSettings.tableStickyOffset 
     * @param {boolean} formSettings.hideLabelIcon 
     */
    formSettings: Object,
    disabled: Boolean
  },
  data: () => ({
    uid: null
  }),
  methods: {
    onInput() {
      let fieldValidator = this.validator && this.validator[this.item.fieldName];
      if (this.item.onChanged != null) {
        this.item.onChanged();
      }
      if (
        fieldValidator &&
        (
          fieldValidator.required === false || 
          (this.item.type === 22 && fieldValidator.htmlValidator === false)
        )
      ) {
        fieldValidator.$touch();
      }
      this.$emit("input");
    },
    onBlur() {
      this.validatorTouch();
      this.$emit("blur");
    },
    onKeyupEnter() {
      this.$emit("keyup:enter");
    },
    sanitizePastedText(event) {
      commons.sanitizePastedText(event, (value) => {
        this.$set(this.formData, this.item.fieldName, value);
      });
    },
    validatorTouch() {
      let field = this.validator && this.validator[this.item.fieldName];
      field && field.$touch();
    },
    getImagePath(path) {
      if (path.startsWith('/s/')) {
        const url = path.split('/');
        const filename = encodeURIComponent(url.pop());
        const encodeURL = `/api/d/${this.vectorId}-${this.item.id}/${url.join("/")}/${filename}`;
        return webAppConfig.baseURL ? `${webAppConfig.baseURL}${encodeURL}` : encodeURL;
      } else {
        const url = path.split('/');
        const filename = encodeURIComponent(url.pop());
        const encodeURL = `${url.join("/")}/${filename}`;
        return webAppConfig.baseURL && !path.startsWith('blob:') ? `${webAppConfig.baseURL}/${encodeURL}` : encodeURL;
      }
    },
    async clientAction(item) {
      let formData = this.formData;
      if (item.actionType === 'customPage') {
        let payload = {
          name: item.label,
          type: item.actionType,
          url: item.actionUrl,
          formData: formData
        }
        if (payload.url) {
          const matchList = [...new Set(payload.url.match(/{formData.[a-z]*(})/gi))];
          matchList.forEach((x) => {
            let value = null;
            try {
              value = eval(x);
            } catch(er) { console.error(err); }
            if (value) {
              payload.url = payload.url.replaceAll(x, JSON.stringify(value));
            }
          });
        }
        const res = await this.$refs.ftsActionPageModal.show(payload);
        if (res) {
          if (item.eventName && item.eventName != "") {
            this.$emit('eventTriggered', {
              eventName: item.eventName,
              eventPayload: res
            });
          }
        }
      } else if (item.eventName && item.eventName != "") {
        this.$emit('eventTriggered', { eventName: item.eventName });
      } else {
        try { eval(item.frontendScript); } catch(err) { console.error(err); }
      }
    }
  },
  computed: {
    getIcon() {
      if (this.formSettings && this.formSettings.hideLabelIcon)
        return;

      const item = this.item;
      if (item.icon) {
        return item.icon;
      } else if (item.due && [5, 6].indexOf(item.type) > -1) {
        return 'fts-icon-calendar-check';
      } else if (item.assignee && [20, 21].indexOf(item.type) > -1) {
        return 'fts-icon-user-check';
      } else if ((item.type === undefined || item.type === null) && item.input) {
        try {
          if (item.input === 'password') {
            return commons.informationIcon(0);
          } else if (item.input === 'toggleSwitch') {
            return commons.informationIcon(1);
          } else {
            return commons.informationIcon(
              commons.informationTypes.find((x) => x.input == item.input).id
            );
          }
        } catch (err) {}
      } else { 
        return commons.informationIcon(item.type);
      }
    },
    labelDisplayName() {
      if (this.item.label)
        return this.item.label;
      else if (this.item.displayName) 
        return this.item.displayName;
      else if (this.item.fieldName)
        return commons.getDisplayName(this.item.fieldName);
    },
    contextField() {
      if (
        clientStore &&
        clientStore.state.workspace &&
        clientStore.state.workspace.contextModuleId &&
        this.item.connectionId
      ) {
        console.log(clientStore.state.workspace);

        return (
          clientStore.state.workspace.contextModuleId.toString() ==
          this.item.connectionId.toString()
        );
      }
      return false;
    },
    required() {
      return this.item.required && !this.item.readOnly;
    },
    error() {
      try {
        let validator = this.validator && this.validator[this.item.fieldName];
        const displayName = this.labelDisplayName;
        if (validator && validator.$error) {
          /* validator error */
          if (this.validatorError)
            try {
              const validatorError = this.validatorError(validator);
              if (validatorError) return validatorError; 
            } catch(err) {
              console.error('FormElement:Validator', err);
              return "Something went wrong.";
            }
          /* email */
          if (validator.email === false)
            return "Please enter email address in format: example@mail.com";
          /* phone */
          if (validator.phone == false)
            return "Please enter valid phone number.";
          /* link */
          if (validator.url === false)
            return "Please enter valid URL address.";
          /* password */
          else if (validator.required && validator.password === false)
            return `${
              displayName ? displayName : "Password"
            } must contain 1 alphabet, 1 numeric, and at least 8 characters.`;
          /* same as */
          else if (validator.required && validator.sameAs === false)
            return `${displayName ? displayName : "Field"} must be identical.`;
          /* max value */
          else if (validator.maxValue === false)
            try {
              return `${displayName ? displayName : "Field"} cannot greater than ${validator.$params.maxValue.max}`;
            } catch(err) {
              console.error(err);
              return `${displayName ? displayName : "Field"} value is too large.`;
            }
          /* min value */
          else if (validator.minValue === false)
            try {
              return `${displayName ? displayName : "Field"} cannot less than ${validator.$params.minValue.min}`; 
            } catch(err) {
              console.error(err);
              return `${displayName ? displayName : "Field"} value is too small.`;
            }
          // format
          else if (validator.format === false) {
            return `Please enter valid format.`;
          }
          /* table */
          else if (this.item.type == 14) {
            if (validator.required === false)
              return `${displayName ? displayName : "Table"} at least must have one row.`;
            /* column */
            const fields = Object.values(validator.$each.$iter)
              .filter(x => x.$invalid)
              .map(x => {
                const columns = Object.values(x);
                let validator = {};
                if (columns.some(y => y.required === false))
                  validator.required = false;
                if (columns.some(y => y.email === false))
                  validator.email = false;
                if (columns.some(y => y.phone === false))
                  validator.phone = false;
                return validator;
              });
            /* column error */
            let error = "";
            if (fields.some(x => x.required === false))
              error += `${displayName ? displayName : "Field"} column(s) cannot be empty.`;
            if (fields.some(x => x.email === false))
              error += " Please enter email address in format: example@mail.com.";
            if (fields.some(x => x.phone === false))
              error += " Please enter valid phone number.";
            
            return error;
          }
          /* default */
          else
            return `${displayName ? displayName : "Field"} is required.`;
        }
      } catch (err) {
        console.error(err)
      }
    },
    submitted() {
      return this.validation && this.validation.submitted;
    },
    fullWidthElement() {
      return (
        this.item.type == 14 ||
        this.item.type == 15 ||
        this.formElementInput === "table" ||
        this.formElementInput === "list"
      );
    },
    formElementDisplay() {
      return (
        this.item.display ||
        this.item.input == "display" ||
        this.item.readOnly ||
        this.view ||
        (this.contextField && this.item.type != 24) ||
        (this.username && this.item.fieldName == 'username')
      );
    },
    formElementInput() {
      if (!commons.isInteger(this.item.type) || this.item.custom) {
        return this.item.input;
      }
    },
    isFormElementSlot() {
      return !!this.$slots.formElement || !!this.$scopedSlots.formElement
    },
  },
  async mounted() {
    this.uid = this._uid;

    if (
      this.contextField &&
      this.item.type != 24 &&
      clientStore &&
      clientStore.state.workspaceContext
    ) {
      this.formData[this.item.fieldName] = clientStore.state.workspaceContext.name;
      this.formData[this.item.fieldName + "Id"] = clientStore.state.workspaceContext.id;
    }
    if (
      !this.item.readOnly &&
      !this.item.display &&
      this.item.fieldName &&
      this.item.input != "display"
    ) {
      if (this.formData[this.item.fieldName] === undefined) {
        Vue.set(this.formData, this.item.fieldName, null);
      }
    }

    if (this.formElementInput == "dropDown") {
      if (this.formData[this.item.fieldName + "Id"] === undefined) {
        Vue.set(this.formData, this.item.fieldName + "Id", 0);
      }

      if (this.formData[this.item.fieldName + "Model"] === undefined) {
        Vue.set(this.formData, this.item.fieldName + "Model", null);
      }
    } else if (
      this.formElementInput == "imagesUpload" ||
      this.formElementInput == "filesUpload"
    ) {
      if (this.formData[this.item.fieldName] === undefined) {
        Vue.set(this.formData, this.item.fieldName, []);
      }
    } else if (this.formElementInput == "user") {
      if (this.formData[this.item.fieldName] === undefined) {
        Vue.set(this.formData, this.item.fieldName + "Model", null);
        Vue.set(this.formData, this.item.fieldName + "Username", null);
        Vue.set(this.formData, this.item.fieldName + "Id", null);
      }
    } else if (this.formElementInput == "table") {
      if (
        this.formData[this.item.fieldName] === undefined ||
        this.formData[this.item.fieldName] === null
      ) {
        Vue.set(this.formData, this.item.fieldName, []);
      }
    }
    /* temp (to-do remove) */
    try {
      if (this.item.type == 7 && this.item.advancedTemplate) {
        const res = this.$route.name === 'shareForm' ? 
          await Axios.post(`${webAppConfig.baseURL}/api/workspace/list`, {
            moduleId: this.item.connectionId,
            currentPage: 0,
            pageSize: 20
          }, {
            headers: {
              Authorization: `inishare ${this.$route.params.id}:${this.$route.query.code}`
            }
          }) : 
          await clientStore.dispatch("loadWorkspaceList", {
            moduleId: this.item.connectionId,
            currentPage: 0,
            pageSize: 20
          });
        const optionsList = res.data.data.list.map((x) => ({
          id: x.id,
          title: x.extension[this.item.advancedTemplate.title],
          image: x.extension[this.item.advancedTemplate.image],
          optionName: x.extension[this.item.advancedTemplate.title],
          value: x.extension[this.item.advancedTemplate.title]
        }));
        this.$set(this.item, "optionList", optionsList);
      }
    } catch(err) { console.error(err); }
    /* end */
  }
};
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.fts-form-element {
  max-width: 100%;
  &.fw {
    flex: 1 1 100%;
  }
  &:not(.fw) {
    flex: 1 1 $form-control-width;
    .fts-form-element-wrapper {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-start;
      > .form-element-content {
        min-width: 0;
        width: 100%;
        flex-grow: 1;
      }
    }
  }
  &.compact-form-design {
    > .fts-form-element-wrapper {
      flex-wrap: nowrap;
    }
  }
  &.comfort-form-design {
    .form-element-checkbox-group {
      .form-element-checkbox-label {
        padding-top: calc(0.375rem + 2.5px);
        padding-bottom: calc(0.375rem + 2.5px);
      }
    }
  }
  .form-element-custom-description {
    color: var(--fts-text);
  }
  .form-element-custom-image {
    max-height: 200px;
    max-width: 100%;
    display: block;
  }
  .form-element-custom-empty-space {
    height: 2rem;
  }
}
.form-element-checkbox-group {
  display: flex;
  align-items: flex-start;
  .form-element-checkbox-label {
    padding: calc(0.375rem + 1px) 0.75rem;
    font-size: 0.875rem;
    line-height: 1.5;
    color: var(--fts-form-label);
    cursor: pointer;
  }
}
.form-element-checkbox {
  width: auto;
  &.form-element-checkbox--error {
    .m-chckbox--group {
      border-color: $input-error-border;
    }
  }
}
</style>